import type { FC, ReactNode } from 'react'
import type { MediaItem } from '@/types'
import type { Tone } from '@/components/generals/info-grid'

import { cn } from '@/lib/utils'
import { MediaFrame } from '@/components/generals/media-frame'
import { SplitReveal } from '@/components/generals/split-reveal'
import { isDark, toneClasses } from '@/components/generals/info-grid'

interface EditorialSplitProps {
    id: string
    image: MediaItem
    caption?: string
    eyebrow: string
    title: ReactNode
    body: string
    details?: string[]
    reverse?: boolean
    tone?: Tone
    /** Buttons or links under the text. */
    actions?: ReactNode
}

/** A large photograph beside a short editorial block, used across the pages. */
export const EditorialSplit: FC<EditorialSplitProps> = ({
    id,
    image,
    caption,
    eyebrow,
    title,
    body,
    details,
    reverse = false,
    tone = 'bone',
    actions
}): ReactNode => {
    const dark = isDark(tone)

    return (
        <section id={id} aria-labelledby={`${id}-heading`} className={cn('relative z-10', toneClasses[tone])}>
            <div className='grid items-center gap-12 px-5 py-20 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:px-12 lg:py-28'>
                <figure className={cn('lg:col-span-7', reverse && 'lg:order-2 lg:col-start-6')}>
                    <MediaFrame
                        image={image}
                        sizes='(min-width: 1024px) 58vw, 100vw'
                        mask={reverse ? 'right' : 'left'}
                        parallax={8}
                        className='aspect-[4/3] rounded-[4px] lg:aspect-[5/4]'
                    />

                    {caption && (
                        <figcaption className={cn('mt-3 font-mono text-[10px] uppercase tracking-[0.2em]', dark ? 'text-bone/55' : 'text-muted-foreground')}>
                            {caption}
                        </figcaption>
                    )}
                </figure>

                <div className={cn('lg:col-span-4', reverse ? 'lg:order-1 lg:col-start-1' : 'lg:col-start-9')}>
                    <p className={cn('font-mono text-[11px] uppercase tracking-[0.24em]', dark ? 'text-bone/60' : 'text-charcoal/55')}>
                        {eyebrow}
                    </p>

                    <SplitReveal
                        as='h2'
                        id={`${id}-heading`}
                        className='mt-5 text-[clamp(2rem,3.4vw,3.3rem)] font-light leading-[1.04] tracking-[-0.03em] [&_em]:font-serif [&_em]:font-normal'
                    >
                        {title}
                    </SplitReveal>

                    <p data-reveal className={cn('mt-6 text-[15px] leading-relaxed', dark ? 'text-bone/75' : 'text-charcoal/75')}>
                        {body}
                    </p>

                    {details && details.length > 0 && (
                        <ul data-reveal className={cn('mt-8 border-t', dark ? 'border-bone/15' : 'border-charcoal/15')}>
                            {details.map((detail) => (
                                <li
                                    key={detail}
                                    className={cn('border-b py-3 font-mono text-[11px] uppercase tracking-[0.18em]', dark ? 'border-bone/15 text-bone/75' : 'border-charcoal/10 text-charcoal/70')}
                                >
                                    {detail}
                                </li>
                            ))}
                        </ul>
                    )}

                    {actions && (
                        <div data-reveal className='mt-8 flex flex-wrap items-center gap-4'>
                            {actions}
                        </div>
                    )}
                </div>
            </div>
        </section>
    )
}
